import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ExternalLink, UserPlus, Target } from "lucide-react";
import ContactFormDialog from "@/components/crm/ContactFormDialog";
import { UK_AGENT_TEMPLATE } from "@/constants/ukAgentTemplate";

/**
 * Renders the static UK-agent template content (8 steps) INSIDE the existing
 * GoalDetail page when goal.template === "uk_agent". Persists per-goal user
 * answers (Step 1 text fields, Step 2 checklist marks) via the existing
 * PATCH /api/career/goals/{goal_id} endpoint (template_data field).
 *
 * No new backend endpoint, no LLM call, no web fetch.
 */
export default function UkAgentGoalContent({ goal, lang, onGoalUpdated }) {
  const L = UK_AGENT_TEMPLATE[lang === "en" ? "en" : "pl"];
  const [step1, setStep1] = useState({});
  const [step2, setStep2] = useState({});
  const [openSections, setOpenSections] = useState(() => new Set(["A"]));
  const [prefill, setPrefill] = useState(null);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  // Guard: don't overwrite server state with the local defaults on first paint.
  const bootRef = useRef(false);

  // Load from goal.template_data on mount / when goal id changes
  useEffect(() => {
    const td = goal?.template_data || {};
    setStep1(td.step1 || {});
    setStep2(td.step2 || {});
    bootRef.current = true;
  }, [goal?.goal_id]);

  const schedulePatch = (nextStep1, nextStep2) => {
    if (!bootRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const r = await api.patch(`/career/goals/${goal.goal_id}`, {
          template_data: { step1: nextStep1, step2: nextStep2 },
        });
        onGoalUpdated?.(r.data);
      } catch {
        // Interceptor swallows 401s post-logout. Everything else is silent
        // too — the debounced save retries on the next keystroke.
      } finally {
        setSaving(false);
      }
    }, 700);
  };

  const onStep1Change = (key, value) => {
    setStep1((prev) => {
      const next = { ...prev, [key]: value };
      schedulePatch(next, step2);
      return next;
    });
  };
  const onStep2Toggle = (idx) => {
    setStep2((prev) => {
      const next = { ...prev, [idx]: !prev[idx] };
      schedulePatch(step1, next);
      return next;
    });
  };

  const toggleSection = (key) => {
    setOpenSections((prev) => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key); else s.add(key);
      return s;
    });
  };

  const openAddToCrm = (agency) => {
    setPrefill({
      first_name: agency.name,
      last_name: "",
      type: "agent_uk",
      company: "",
      email: agency.email || "",
      project_context: agency.website || "",
      notes: agency.website ? (lang === "pl" ? `Strona: ${agency.website}` : `Website: ${agency.website}`) : "",
    });
  };

  return (
    <div className="mt-2" data-testid="uk-agent-goal-content">
      <div className="flex items-center gap-2 mb-6">
        <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase flex items-center gap-1">
          <Target size={11} /> {L.eyebrow}
        </div>
        {saving && (
          <span className="mono text-[10px] uppercase tracking-widest text-stone-500">
            {lang === "pl" ? "zapisuję…" : "saving…"}
          </span>
        )}
      </div>

      <div className="space-y-12">
        {L.salesDescription && (
          <section
            data-testid="uk-agent-sales-description"
            className="paper p-6 border-amber-500/30 bg-amber-500/5"
          >
            <p className="text-stone-200 text-sm leading-relaxed whitespace-pre-line">
              {L.salesDescription}
            </p>
          </section>
        )}
        {L.preSection && (
          <section
            data-testid="uk-agent-pre-section"
            className="paper p-6 border-amber-500/40 bg-amber-500/5"
          >
            <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">
              {lang === "pl" ? "Ważne przed startem" : "Important before you start"}
            </div>
            <h2 className="serif text-2xl sm:text-3xl text-stone-100 mt-1">
              {L.preSection.title}
            </h2>
            <div className="mt-5 space-y-5">
              {L.preSection.blocks.map((b, i) => (
                <div key={i}>
                  {b.heading && (
                    <h3 className="serif text-lg text-amber-100">{b.heading}</h3>
                  )}
                  <p className="text-stone-200 text-sm leading-relaxed whitespace-pre-line mt-1">
                    {b.body}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-5">
              <a
                data-testid="uk-agent-pre-section-link"
                href={L.preSection.linkHref}
                target="_blank"
                rel="noopener noreferrer"
                className="ghost-btn !py-2 !text-xs"
              >
                <ExternalLink size={12} /> {L.preSection.linkLabel}
              </a>
            </div>
          </section>
        )}
        {L.steps.map((step, idx) => (
          <motion.section
            key={idx}
            data-testid={`uk-agent-step-${idx + 1}`}
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">
              {L.stepLabel} {String(idx + 1).padStart(2, "0")}
            </div>
            <h2 className="serif text-2xl sm:text-3xl text-stone-100 mt-1">{step.title}</h2>

            {step.body && (
              <div className="text-stone-300 leading-relaxed mt-4 whitespace-pre-line">
                {step.body}
              </div>
            )}

            {step.fields && (
              <ul className="mt-5 space-y-4">
                {step.fields.map((f, i) => {
                  const key = `f${i}`;
                  return (
                    <li key={i} className="paper p-4">
                      <label className="serif text-lg text-stone-100 block">
                        {f.label}
                      </label>
                      <div className="text-stone-400 text-sm mt-1">{f.hint}</div>
                      <textarea
                        data-testid={`uk-agent-step1-input-${i}`}
                        value={step1[key] || ""}
                        onChange={(e) => onStep1Change(key, e.target.value)}
                        rows={3}
                        placeholder={lang === "pl" ? "Twoja odpowiedź…" : "Your answer…"}
                        className="script-input mono text-sm mt-3 w-full"
                      />
                    </li>
                  );
                })}
              </ul>
            )}

            {step.checklist && (
              <ul className="mt-5 space-y-2">
                {step.checklist.map((raw, i) => {
                  const c = typeof raw === "string" ? { label: raw } : raw;
                  return (
                    <li key={i}>
                      <label className="paper p-3 flex items-start gap-3 cursor-pointer hover:border-amber-500/50">
                        <input
                          type="checkbox"
                          data-testid={`uk-agent-check-${idx + 1}-${i}`}
                          checked={!!step2[i]}
                          onChange={() => onStep2Toggle(i)}
                          className="w-4 h-4 accent-amber-500 mt-0.5"
                        />
                        <span className="flex-1 min-w-0">
                          <span className={`text-sm ${step2[i] ? "text-stone-500 line-through" : "text-stone-200"}`}>
                            {c.label}
                          </span>
                          {c.hint && (
                            <span className="block text-stone-400 text-xs mt-1 leading-relaxed">{c.hint}</span>
                          )}
                        </span>
                      </label>
                      {c.callout && (
                        <div
                          data-testid={`uk-agent-callout-${idx + 1}-${i}`}
                          className="mt-2 paper p-4 border-amber-500/30 bg-amber-500/5"
                        >
                          <div className="serif text-amber-100 text-base">{c.callout.title}</div>
                          <p className="text-amber-100/90 text-sm mt-2 leading-relaxed whitespace-pre-line">
                            {c.callout.body}
                          </p>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {step.effect && (
              <div className="mt-5 paper p-4 border-amber-500/30 bg-amber-500/5 text-amber-100 text-sm whitespace-pre-line">
                {step.effect}
              </div>
            )}

            {step.sections && step.sections.map((sec) => (
              <div key={sec.key} className="mt-6">
                <button
                  data-testid={`uk-agent-section-${sec.key}`}
                  onClick={() => toggleSection(sec.key)}
                  className="w-full paper p-4 text-left flex items-center justify-between hover:border-amber-500/50"
                >
                  <span className="serif text-xl text-stone-100">{sec.title}</span>
                  <span className="mono text-[10px] uppercase tracking-widest text-amber-500">
                    {openSections.has(sec.key) ? L.collapse : L.expand}
                  </span>
                </button>
                {openSections.has(sec.key) && (
                  <ul className="mt-3 space-y-2">
                    {sec.agencies.map((a, i) => (
                      <li key={i} className="paper p-4">
                        <div className="serif text-lg text-stone-100">{a.name}</div>
                        {a.website && (
                          <div className="mono text-xs text-stone-400 mt-1 break-all">{a.website}</div>
                        )}
                        {a.email ? (
                          <div className="mono text-xs text-amber-500/80 mt-1 break-all">{a.email}</div>
                        ) : (
                          <div className="mono text-[11px] text-stone-500 mt-1 italic">{L.noEmailNote}</div>
                        )}
                        {a.note && (
                          <div className="text-stone-300 text-sm mt-2">{a.note}</div>
                        )}
                        <div className="mt-3 flex gap-2 flex-wrap">
                          {a.website && (
                            <a
                              data-testid={`uk-agent-open-${a.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                              href={`https://${a.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ghost-btn !py-2 !text-xs"
                            >
                              <ExternalLink size={12} /> {L.openWebsite}
                            </a>
                          )}
                          <button
                            data-testid={`uk-agent-add-${a.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                            onClick={() => openAddToCrm(a)}
                            className="pill-btn !py-2 !text-xs"
                          >
                            <UserPlus size={12} /> {L.addToCrm}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {step.underListNote && (
              <div className="mt-6 text-stone-300 leading-relaxed whitespace-pre-line">
                {step.underListNote}
              </div>
            )}

            {step.moreLinks && (
              <div className="mt-4">
                <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase mb-2">
                  {step.moreLinksHeading}
                </div>
                <ul className="space-y-2">
                  {step.moreLinks.map((m, i) => (
                    <li key={i} className="paper p-3">
                      <div className="text-stone-100 text-sm">{m.name}</div>
                      <a
                        href={`https://${m.website.replace(/^https?:\/\//, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mono text-xs text-amber-500 hover:text-amber-400 break-all"
                      >
                        {m.website}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.section>
        ))}
      </div>

      <ContactFormDialog
        open={prefill !== null}
        onOpenChange={(v) => { if (!v) setPrefill(null); }}
        prefill={prefill}
        onSaved={() => { setPrefill(null); toast.success(lang === "pl" ? "Dodano do CRM." : "Added to CRM."); }}
      />
    </div>
  );
}
